import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const Services = () => {
  const [activeTab, setActiveTab] = useState("contract-farmers");
  const [key, setKey] = useState(0); // Add a key state to force re-render

  // Enhanced animation variants for tab content
  const tabContentVariants = {
    hidden: { 
      opacity: 0,
      y: 50,
      scale: 0.95,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    visible: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      y: -50,
      scale: 0.95,
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    }
  };

  // Enhanced animation variants for text elements
  const textVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      transition: {
        duration: 0.3
      }
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setKey(prevKey => prevKey + 1); // Increment key to force re-render
  };

  return (
    <section className="section3" id="section3">
      <motion.h2 
        className="section-heading"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        Our Services
      </motion.h2>
      <div className="tabs">
        <div className="tab-container">
          <motion.button
            className={`tab-btn ${activeTab === "contract-farmers" ? "active" : ""}`}
            onClick={() => handleTabChange("contract-farmers")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <i className="fa-solid fa-award"></i>
            Organic Certification
          </motion.button>
          <motion.button
            className={`tab-btn ${activeTab === "organic-certification" ? "active" : ""}`}
            onClick={() => handleTabChange("organic-certification")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <i className="fa-solid fa-leaf"></i>
            Vartoh Organic
          </motion.button>
          <motion.button
            className={`tab-btn ${activeTab === "extension-services" ? "active" : ""}`}
            onClick={() => handleTabChange("extension-services")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <i className="fa-solid fa-store"></i>
            Himalay Frsh Store
          </motion.button>
        </div>
      </div>

      <AnimatePresence mode="wait" key={key}>
        {activeTab === "contract-farmers" && (
          <motion.div
            className="tab-content"
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            key="contract-farmers"
          >
            <motion.h4 variants={textVariants}>Organic certification services</motion.h4>
            <motion.p variants={textVariants}>
              Mevedir is committed to empowering farmers through its Internal
              Control System (ICS) projects, which operate across three districts
              in Sikkim. Through these projects we offer services for Organic
              Certification, we enable farmers to maintain their organic status of
              their farms, improve crop quality and production, and secure better
              market prices for their organic produce.
            </motion.p>
            <motion.h4 variants={textVariants}>Training and Facilitation</motion.h4>
            <motion.p variants={textVariants}>
              We provide hands-on training and facilitation for organic farming
              practices. We work with Farmer Producer Organizations (FPOs) to
              source farmers produce, help farmers market their produce and build
              resilience in their operations. We actively buy produce from FPOs,
              fostering business relationships that ensure long-term
              sustainability.
            </motion.p>
            <motion.h4 variants={textVariants}>A Vital Link for Farmers</motion.h4>
            <motion.p variants={textVariants}>
              Mevedir serves as a critical bridge between government programs and
              local farmers, ensuring they access benefits and resources
              effectively. We help amplify farmers growth, unlock new
              opportunities, and reinforce the foundation of sustainable
              agriculture in Sikkim.
            </motion.p>
          </motion.div>
        )}

        {activeTab === "organic-certification" && (
          <motion.div
            className="tab-content"
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            key="organic-certification"
          >
            <motion.h4 variants={textVariants}>A Tribute to Nature an Purity</motion.h4>
            <motion.p variants={textVariants}>
              In the Lepcha language, Vartoh means "Environment," reflecting the
              deep connection of the indigenous Lepcha people of Sikkim with
              nature. Mt. Khangchendzonga, is revered as the guardian deity of
              Sikkim. Sikkim holds the unique distinction of being the world's
              first fully certified organic state, earning recognition for its
              commitment to sustainable farming practices. Vartoh branded products
              are proudly sourced from the pristine Sikkim Himalayas, offering
              safe, nutritious, and pesticide-free organic produce. Free from
              harmful chemicals, these products support healthy living while
              preserving the purity of the environment. Vartoh embodies the
              essence of organic living, environmental harmony, Mt.
              Khangchendzonga, the Himalayas, Sikkim's certified organic status,
              and safe, healthy consumption.
            </motion.p>
          </motion.div>
        )}

        {activeTab === "extension-services" && (
          <motion.div
            className="tab-content"
            variants={tabContentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            key="extension-services"
          >
            <motion.h4 variants={textVariants}>Himalay Frsh Organic Retail Chain</motion.h4>
            <motion.p variants={textVariants}>
              Mevedir, proudly promotes its organic farm produce through its
              environmentally controlled retail outlets, Himalay Frsh, located in
              Sikkim, North Bengal, and Kolkata. Under its flagship brand, Vartoh
              Organic, Mevedir sources premium-quality materials directly from
              Sikkim, providing a reliable market for local farmers while
              supporting their livelihoods. The Himalay Frsh stores feature a
              diverse selection of fresh, organic, and natural products, including
              fermented and dehydrated spices and vegetables, seasonal fruits,
              dairy products, pickles, sauces, cereals, pulses, flours, minor
              forest produce, herbal items, teas, and processed beverages.
              Alongside Vartoh Organic goods, customers can explore local
              favorites such as Temi Tea, Sikkim Supreme, pickles, processed
              meats, and cosmetics. Each product celebrates Sikkim's vibrant
              heritage while delivering high-quality, healthy food to meet modern
              consumer demands.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Services;
